import { React, useState, useEffect } from "react";
import {
  FolderKanban,
  Loader2,
  Server,
  Database,
  Cpu,
  GitBranch,
  Globe,
  Code,
  FlaskConical,
  Rocket,
  MessageSquare,
  PauseCircle,
} from "lucide-react";
import { getAssignedProjects } from "../../Services/ProjectService";
import { useNavigate } from "react-router-dom";
import { getImageUrl } from "../Utils/urlHelper";
import frameBg from "../../Assets/FrameBGDashboard.png";
import { globalSearch } from "../Utils/globalSearch";
import NotificationAlert from "../Alerts/NotificationAlert"

const MyProjects = () => {
  const [alert, setAlert] = useState(null);
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showEnvironmentModal, setShowEnvironmentModal] = useState(false);
  const [selectedProjectEnvironments, setSelectedProjectEnvironments] =
    useState([]);
  const [selectedEnvironment, setSelectedEnvironment] = useState(null);

  const handleSearch = (value) => {
    setSearchQuery(value);
  };

  const handleStatusChange = (value) => {
    setStatusFilter(value);
  };

  useEffect(() => {
    fetchAssignedProjects();
  }, []);

  const fetchAssignedProjects = async () => {
    try {
      const response = await getAssignedProjects();
      setProjects(response.data.assignedProjects || []);
      setLoading(false);
    } catch (error) {
      setAlert({
        type: "error",
        message: "Failed to load projects.",
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const s = status?.toLowerCase();

    switch (s) {
      case "development":
        return {
          color: "bg-blue-100 text-blue-700",
          icon: <Code size={12} />,
        };

      case "uat":
        return {
          color: "bg-yellow-100 text-yellow-700",
          icon: <FlaskConical size={12} />,
        };

      case "production":
        return {
          color: "bg-green-100 text-green-700",
          icon: <Rocket size={12} />,
        };

      case "discussion":
        return {
          color: "bg-purple-100 text-purple-700",
          icon: <MessageSquare size={12} />,
        };

      case "hold":
        return {
          color: "bg-red-100 text-red-700",
          icon: <PauseCircle size={12} />,
        };

      default:
        return {
          color: "bg-gray-100 text-gray-600",
          icon: null,
        };
    }
  };

  const filteredProjects = globalSearch(projects, searchQuery, [
    "projectName",
    "projectDescription",
    "projectType",
    "projectStatus",
    "projectMangers",
  ]);

  const finalProjects = filteredProjects.filter((p) =>
    statusFilter
      ? p.projectStatus?.toLowerCase() === statusFilter.toLowerCase()
      : true,
  );

  return (
    <div className="min-h-screen w-full px-6 py-6 bg-gray-50">
      {/* HEADER */}
      <div
        className="relative p-6 rounded-2xl shadow-md mb-6 text-white overflow-hidden"
        style={{
          backgroundImage: `url(${frameBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="relative z-10">
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <FolderKanban />
            My Assigned Projects
          </h2>
          <p className="text-sm opacity-90 mt-1">Projects assigned to you</p>
        </div>
      </div>

      {/* LOADING */}
      {loading && (
        <div className="flex justify-center mt-20">
          <Loader2 className="animate-spin text-[#003a63]" size={40} />
        </div>
      )}

      {/* SEARCH + FILTER */}
      {!loading && (
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="text"
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="px-3 py-2 text-sm border rounded-md w-60 focus:outline-none focus:ring-1 focus:ring-blue-400"
            />

            <select
              value={statusFilter}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="px-3 py-2 text-sm border rounded-md"
            >
              <option value="">All Status</option>
              <option value="Development">Development</option>
              <option value="UAT">UAT</option>
              <option value="Production">Production</option>
              <option value="Discussion">Discussion</option>
              <option value="Hold">Hold</option>
            </select>
          </div>
        </div>
      )}

      {/* EMPTY STATE (FILTERED) */}
      {!loading && finalProjects.length === 0 && (
        <div className="flex flex-col items-center justify-center mt-20 text-gray-500 text-center">
          <FolderKanban size={50} className="opacity-40 mb-3" />

          <h3 className="text-lg font-semibold text-gray-700">
            No Projects Found
          </h3>

          <p className="text-sm mt-1">
            {searchQuery || statusFilter
              ? "No matching projects for your filters"
              : "No projects assigned"}
          </p>

          {(searchQuery || statusFilter) && (
            <button
              onClick={() => {
                setSearchQuery("");
                setStatusFilter("");
              }}
              className="mt-3 text-blue-600 text-sm hover:underline"
            >
              Clear Filters
            </button>
          )}
        </div>
      )}

      {/* GRID */}
      {!loading && finalProjects.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {finalProjects.map((p) => (
            <div
              key={p.projectUID}
              className="bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden"
            >
              <div className="h-1.5 bg-gradient-to-r from-blue-500 to-cyan-400" />

              <div className="p-5">
                {/* HEADER */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-28 h-14 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden">
                    {p.logoURL ? (
                      <img
                        src={getImageUrl(p.logoURL)}
                        alt="logo"
                        className="w-full h-full object-contain p-1"
                      />
                    ) : (
                      <FolderKanban className="text-blue-500" size={20} />
                    )}
                  </div>

                  <div className="flex-1">
                    <h3 className="text-md font-semibold text-gray-800">
                      {p.projectName}
                    </h3>
                    <p className="text-xs text-gray-400">
                      {p.projectType || "Project"}
                    </p>
                  </div>

                  {(() => {
                    const badge = getStatusBadge(p.projectStatus);
                    return (
                      <span
                        className={`text-xs px-3 py-1 rounded-full font-medium flex items-center gap-1 ${badge.color}`}
                      >
                        {badge.icon}
                        {p.projectStatus || "N/A"}
                      </span>
                    );
                  })()}
                </div>

                {/* DETAILS */}
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between text-gray-600">
                    <span className="flex items-center gap-2">
                      <Cpu size={14} /> Tech Stack
                    </span>
                    <span className="text-gray-800 font-medium truncate max-w-[55%]">
                      {p.techStack || "N/A"}
                    </span>
                  </div>

                  <div className="flex justify-between text-gray-600">
                    <span>👤 Project Managers</span>
                    <span className="text-gray-800 font-medium text-right max-w-[55%] truncate">
                      {p.projectManagers || "N/A"}
                    </span>
                  </div>

                  <div className="flex justify-between text-gray-600">
                    <span className="flex items-center gap-2">
                      <Server size={14} /> Server
                    </span>
                    <span className="text-gray-800 font-medium">
                      {p.ipAddress || "N/A"}
                    </span>
                  </div>

                  <div className="flex justify-between text-gray-600">
                    <span className="flex items-center gap-2">
                      <Database size={14} /> Database
                    </span>
                    <span className="text-gray-800 font-medium">
                      {p.dbDetails || "N/A"}
                    </span>
                  </div>

                  <div className="flex justify-between text-gray-600">
                    <span className="flex items-center gap-2">
                      <GitBranch size={14} /> Git Repository
                    </span>
                    <span className="font-medium text-gray-800 flex flex-col items-end text-right">
                      {p.gitRepoPath
                        ? p.gitRepoPath.split(",").map((repo, i) => {
                            const cleanRepo = repo.trim();
                            return (
                              <a
                                key={i}
                                href={
                                  cleanRepo.startsWith("http")
                                    ? cleanRepo
                                    : `https://${cleanRepo}`
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline truncate max-w-[180px]"
                                title={cleanRepo}
                              >
                                {cleanRepo.split("/").pop()}
                              </a>
                            );
                          })
                        : "N/A"}
                    </span>
                  </div>
                </div>

                {/* FOOTER */}
                <div className="mt-5 flex justify-between items-center">
                  {p.environments && p.environments.length > 0 ? (
                    <button
                      onClick={() => {
                        setSelectedProjectEnvironments(p.environments);

                        setSelectedEnvironment(null);

                        setShowEnvironmentModal(true);
                      }}
                      className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                    >
                      <Globe size={14} />
                      Open Application
                    </button>
                  ) : (
                    <span className="text-sm text-gray-400 flex items-center gap-1 cursor-not-allowed">
                      <Globe size={14} />
                      Open Application
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Environment Details Modal */}
      {showEnvironmentModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-fadeIn">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#0061A4] to-[#00ACED] px-6 py-4 flex items-center justify-between">
              <h2 className="text-white text-lg font-semibold">
                Environment Details
              </h2>

              <button
                onClick={() => setShowEnvironmentModal(false)}
                className="text-white text-3xl hover:text-red-500"
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div className="p-6">
              {!selectedEnvironment ? (
                <>
                  <h3 className="text-sm font-semibold text-gray-700 mb-4">
                    Select Environment
                  </h3>

                  <div className="space-y-3">
                    {selectedProjectEnvironments.map((env) => (
                      <div
                        key={env.environmentUID}
                        className="border border-gray-200 rounded-xl p-4 hover:border-[#00ACED] hover:bg-blue-50 transition"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div
                            className="flex-1 cursor-pointer"
                            onClick={() =>
                              setSelectedEnvironment({
                                ...env,
                                loginType: Number(env.loginType),
                              })
                            }
                          >
                            {/* Top Row */}
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="font-semibold text-gray-800">
                                {env.environmentType}
                              </h4>

                              <span
                                className={`text-[10px] px-2.5 py-1 rounded-full font-medium ${
                                  Number(env.loginType) === 1
                                    ? "bg-green-100 text-green-700"
                                    : "bg-orange-100 text-orange-700"
                                }`}
                              >
                                {Number(env.loginType) === 1
                                  ? "CLS"
                                  : "Non-CLS"}
                              </span>
                            </div>

                            {/* URL */}
                            <p className="text-xs text-gray-500 mt-2 break-all">
                              {env.appURL}
                            </p>
                          </div>

                          {/* Open Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();

                              const url = env.appURL?.startsWith("http")
                                ? env.appURL
                                : `https://${env.appURL}`;

                              window.open(url, "_blank");
                            }}
                            className="bg-gradient-to-r from-[#0061A4] to-[#00ACED] text-white text-xs px-4 py-2 rounded-lg hover:opacity-90 whitespace-nowrap"
                          >
                            Open Application
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="space-y-5">
                  {/* Back */}
                  <button
                    onClick={() => setSelectedEnvironment(null)}
                    className="text-sm text-blue-600 hover:underline"
                  >
                    ← Back to Environments
                  </button>

                  {/* Environment Type */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                      Environment Type
                    </label>

                    <div className="border rounded-lg px-4 py-3 bg-gray-50 text-sm">
                      {selectedEnvironment.environmentType || "-"}
                    </div>
                  </div>

                  {/* Login Type */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                      Login Type
                    </label>

                    <div className="border rounded-lg px-4 py-3 bg-gray-50 text-sm">
                      {Number(selectedEnvironment.loginType) === 1
                        ? "CLS Based"
                        : "Non-CLS Based"}
                    </div>
                  </div>

                  {/* URL */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                      Application URL
                    </label>

                    <div className="border rounded-lg px-4 py-3 bg-gray-50 text-sm break-all">
                      {selectedEnvironment.appURL || "-"}
                    </div>
                  </div>

                  {/* Non CLS Credentials */}
                  {Number(selectedEnvironment.loginType) === 2 && (
                    <>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                          Username / Email
                        </label>

                        <div className="border rounded-lg px-4 py-3 bg-gray-50 text-sm">
                          {selectedEnvironment.username || "-"}
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                          Password
                        </label>

                        <div className="border rounded-lg px-4 py-3 bg-gray-50 text-sm">
                          {selectedEnvironment.password || "-"}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Launch */}
                  <button
                    onClick={() => {
                      const url = selectedEnvironment.appURL?.startsWith("http")
                        ? selectedEnvironment.appURL
                        : `https://${selectedEnvironment.appURL}`;

                      window.open(url, "_blank");
                    }}
                    className="w-full bg-gradient-to-r from-[#0061A4] to-[#00ACED] text-white py-3 rounded-xl font-medium hover:opacity-90"
                  >
                    Open Application
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {alert && (
        <NotificationAlert
          type={alert.type}
          message={alert.message}
          show={true}
          duration={4000}
          onClose={() => setAlert(null)}
        />
      )}

    </div>
  );
};

export default MyProjects;
